<template>
  <layout-default>
    <template v-slot:toolbar>
      <div class="practice_filters" v-if="isRoleAllowed">
        <v-btn text rounded elevation="0" color="accent" @click="getTemplate">
          <v-icon>bhi-download</v-icon>Download template
        </v-btn>
        <file-upload
          class="upload-file-area mr-2"
          :class="isTaskInProgress ? 'disabled' : ''"
          :disabled="isTaskInProgress"
          :multiple="false"
          :accept="getMimeTypes(23)"
          @input-file="inputFile"
          ref="upload"
        >
          <v-btn rounded elevation="0" color="accent">
            <v-icon>bhi-upload</v-icon>Upload participants
          </v-btn>
        </file-upload>
      </div>
    </template>
    <div id="practice" class="v-inner--wrap">
      <v-overlay :value="loading" z-index="4">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <v-dialog
        v-model="showUploadAdvisorSelectPopup"
        persistent
        max-width="290"
        @click:outside="onClickOutside"
        @keydown="onKeydown"
      >
        <v-card>
          <v-card-title class="headline">You must select Advisor</v-card-title>
          <v-card-text
            >Participants must have assigned Advisor to their account. All
            Participants will be assigned to selected Advisor from list below.
            Please make sure you are uploading correct list.<br />
            <bh-select
              class="mb-3"
              :items="computedAdvisors"
              item-text="fullName"
              item-value="id"
              :value="advisorId"
              @input="advisorId = $event"
          /></v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="rounded-pill"
              text
              @click="closeUploadAdvisorSelectPopup"
              >Cancel</v-btn
            >
            <v-btn
              class="rounded-pill elevation-0"
              color="accent"
              @click="proceedUpload"
              :disabled="!advisorId"
              >Confirm</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <inner-drawer :routes="practicesPaths">
        <template v-slot:header>
          <practice-card
            @updateAvatar="updateAvatar"
            :get-mime-types="getMimeTypes"
            :user="practice"
          />
        </template>
      </inner-drawer>
      <inner-content />
    </div>
  </layout-default>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import InnerDrawer from '@/components/InnerDrawer.vue'
import InnerContent from '@/components/InnerContent.vue'
import PracticeCard from '@/components/practices/PracticeCard.vue'
import LayoutDefault from '../layouts/LayoutDefault.vue'
import FileUpload from 'vue-upload-component'
import BhSelect from '@/components/inputs/BhSelect.vue'
import axios from 'axios'
import { childrenPaths as practicesPaths } from '@/router/paths/practicesPaths'
import { FILE_SIZE_LIMIT, FILE_SIZE_LIMIT_NAME } from '@/helpers/constants'

export default {
  name: 'Practice',
  components: {
    InnerDrawer,
    InnerContent,
    PracticeCard,
    LayoutDefault,
    FileUpload,
    BhSelect,
  },
  data() {
    return {
      practicesPaths,
      uploadFile: null,
      showUploadAdvisorSelectPopup: false,
      advisorId: false,
      practiceAdvisors: [],
    }
  },
  mounted() {
    this.getPractice(this.practiceId)
  },
  computed: {
    ...mapState('practicesStates', ['practice', 'loading']),
    ...mapState('userStates', ['profileId', 'tasks']),
    ...mapGetters('userStates', ['getRolename']),
    ...mapGetters('dictionariesStates', ['getMimeTypes']),
    ...mapState('dictionariesStates', [
      'dictionaryBhUsersRoleIds',
      'dictionaryPracticeUserRole',
    ]),
    advisorQueryParams() {
      return {
        roleIds: [this.dictionaryPracticeUserRole.Advisor],
        isActive: true,
        sortBy: 'fullName',
        isAvailable: true,
      }
    },
    advisorsQuery() {
      const params = new URLSearchParams()
      for (const [key, value] of Object.entries(this.advisorQueryParams)) {
        if (Array.isArray(value)) {
          value.forEach(item => {
            params.append(key, item)
          })
        } else if (value) {
          params.append(key, value)
        }
      }
      return params
    },
    computedAdvisors() {
      return this.practiceAdvisors.map(doctor => ({
        ...doctor,
        fullName: `${doctor.firstName} ${doctor.lastName}`,
      }))
    },
    practiceId() {
      return this.$route.params.id
    },
    isRoleAllowed() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ==
          this.dictionaryPracticeUserRole.Administrator ||
        this.dictionaryBhUsersRoleIds[this.getRolename] ==
          this.dictionaryBhUsersRoleIds.Admin ||
        this.dictionaryBhUsersRoleIds[this.getRolename] ==
          this.dictionaryBhUsersRoleIds.TechnicalSupport
      )
    },
    isTaskInProgress() {
      return this.tasks.findIndex(task => task.status == 'in_progress') != -1
        ? true
        : false
    },
  },
  methods: {
    ...mapActions('practicesStates', [
      'getPractice',
      'getPracticeUsers',
      'updatePracticePicture',
    ]),
    ...mapActions('patientsStates', [
      'uploadParticipants',
      'getParticipantsUploadTemplate',
    ]),
    ...mapActions('userStates', ['getTasks']),
    ...mapMutations('appStates', ['setSnackbarNotification']),
    async updateAvatar(data) {
      this.updatePracticePicture({
        id: this.practiceId,
        file: data,
      })
    },
    convertImageToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = error => reject(error)
      })
    },
    closeUploadAdvisorSelectPopup() {
      this.showUploadAdvisorSelectPopup = false
      this.uploadFile = null
    },
    proceedUpload() {
      if (!this.$refs.upload.active && this.advisorId && this.uploadFile) {
        this.$refs.upload.active = true
        this.uploadParticipants({
          file: this.uploadFile,
          advisorId: this.advisorId,
        })
        this.getTasks()
        this.closeUploadAdvisorSelectPopup()
        this.practiceAdvisors = []
        this.advisorId = null
      }
    },
    async getAdvisors() {
      const advisors = await this.getPracticeUsers({
        id: this.practiceId,
        params: this.advisorsQuery,
        returnData: true,
      })

      this.practiceAdvisors = advisors || []
    },
    inputFile(newFile, oldFile) {
      if (newFile && !oldFile) {
        if (newFile.size < FILE_SIZE_LIMIT) {
          this.uploadFile = newFile.file
          this.showUploadAdvisorSelectPopup = true
          this.getAdvisors()
        } else {
          this.setSnackbarNotification(
            `Max upload file size is ${FILE_SIZE_LIMIT_NAME}. Try again with smaller file.`,
          )
        }
      }
    },
    getTemplate() {
      this.getParticipantsUploadTemplate().then(url => {
        console.log(url)
        axios
          .get(url.fileUrl, { responseType: 'blob' })
          .then(response => {
            const blob = new Blob([response.data], {
              type:
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;',
            })
            const link = document.createElement('a')
            link.href = URL.createObjectURL(blob)
            link.download = 'participants_import_template.xlsx'
            link.click()
            URL.revokeObjectURL(link.href)
          })
          .catch(console.error)
      })
    },
    onClickOutside() {
      this.closeUploadAdvisorSelectPopup()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.closeUploadAdvisorSelectPopup()
    },
  },
  watch: {
    $route(to, from) {
      // get new current practice data when practice id changed
      if (to.params?.id !== undefined && from.params?.id !== to.params?.id) {
        this.getPractice(to.params.id)
      }
    },
  },
}
</script>
<style lang="scss">
.practice_filters {
  display: flex;
  flex-grow: 1;
  justify-content: flex-end;
  width: 100%;
  .upload-file-area {
    transition-duration: 300ms;
    &.disabled {
      opacity: 0.5;
      cursor: default;
    }
  }
  .v-btn {
    margin-right: 10px;
    .v-icon {
      margin-right: 10px;
    }
  }
  .file-uploads label {
    cursor: pointer;
  }
}
</style>
